import { newTileLayer, newWMSLayer, newMonitoredWMSLayer } from "features/map/services/map-layers";
import { MapRestLayerProps, MapTileLayerProps } from "features/map/types/layers";
import { HardFailInfo } from "features/map/utils/layerHealthMonitor";
import { FirestoreExternalWMSDataLayer, FirestoreRESTLayer } from "types/firestoreFeatures";

// const WITH_LAYER_FAIL_DEBUG = false;
// --- Background layers --- //
// Leaflet layer instances may only be attached to one map at a time. Keep the
// configuration here and create a new instance for each map (including print maps).
export function createDefaultBackgroundLayers(): MapTileLayerProps[] {
  return [
    {
      id: "googleHybrid",
      countryCode: "aaa",
      mode: "dark",
      leafletObj: newTileLayer("https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", {
        detectRetina: false,
        maxZoom: 20,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Google",
      }),
    },
    {
      id: "googleTerrain",
      countryCode: "aaa",
      mode: "dark",
      leafletObj: newTileLayer("https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", {
        detectRetina: false,
        maxZoom: 20,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Google",
      }),
    },
    {
      id: "osmStandard",
      countryCode: "aaa",
      mode: "light",
      leafletObj: newTileLayer("https://{s}.tile.osm.org/{z}/{x}/{y}.png", {
        detectRetina: false,
        maxZoom: 19,
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      }),
    },
  ];
}

// The live map uses one set of layers for its lifetime. Print maps must use
// createDefaultBackgroundLayers() to avoid sharing these instances.
export const defaultBackgroundLayers = createDefaultBackgroundLayers();

export function convertFirestoreWMSDataToWMSLayer(
  map: L.Map,
  data: FirestoreExternalWMSDataLayer,
  errorCallback?: (info: HardFailInfo) => void
): MapTileLayerProps {
  const id = data.name;
  const { mode, type, ...wmsOptions } = data.options;
  const optMode = mode ? mode : ("dark" as "dark");
  const countryCode = data.countryCode ? data.countryCode : "zzz";
  const label = data.label ? data.label : undefined;

  // Patch wms options if needed
  let wmsOptionsPatched = { ...wmsOptions, label: label ? label : id };
  if (type === "overlay") {
    wmsOptionsPatched.printExcludable = true;
  }
  // Check if leaflet object already exists
  let leafletObj = null;
  map.eachLayer((layer) => {
    //@ts-ignore
    if (layer.options.id === id) {
      leafletObj = layer;
    }
  });
  if (!leafletObj) {
    let url = data.url;
    // if (WITH_LAYER_FAIL_DEBUG && ["protectedForestDK", "coastelProtectionLineDK", "cadastralDK", "protectedNatureDK"].includes(id)) {
    //   // Slice the last 4 characters and add CRAP!!! to simulate error
    //   url = url.slice(0,-4) + "CRAP!!!";
    // }
    if (!errorCallback) {
      leafletObj = newWMSLayer(url, wmsOptionsPatched);
    } else {
      const { layer } = newMonitoredWMSLayer(
        url,
        wmsOptionsPatched,
        id,
        label ? label : id,
        errorCallback
      );
      leafletObj = layer;
    }
  }
  return { id, countryCode, label, legend: data.legend, mode: optMode, leafletObj: leafletObj };
}

export function getWMSList(
  map: L.Map,
  firestoreLayers: { [key: string]: FirestoreExternalWMSDataLayer },
  type: "background" | "overlay",
  removedLayers?: string[],
  errorCallback?: (info: HardFailInfo) => void
): MapTileLayerProps[] {
  let layers = Object.values(firestoreLayers)
    .filter((layer) => layer.options.type === type && !removedLayers?.includes(layer.name))
    .map((layer) => {
      return convertFirestoreWMSDataToWMSLayer(map, layer, errorCallback);
    });
  return layers;
}

export function getRESTList(
  firestoreLayers: { [key: string]: FirestoreRESTLayer },
  removedLayers?: string[]
): MapRestLayerProps[] {
  let layers = Object.values(firestoreLayers)
    .filter((layer) => !removedLayers?.includes(layer.name))
    .map((layer) => {
      return {
        id: layer.name,
        countryCode: layer.countryCode,
        label: layer.label,
        isRest: true,
        leafletObj: null,
      };
    });
  return layers;
}
